import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, Gift, BookOpen, Sparkles, Users } from 'lucide-react';

// Use relative path for API calls
const API = '/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentType, setPaymentType] = useState(null);
  const [message, setMessage] = useState('Ödeme doğrulanıyor...');

  useEffect(() => {
    processPayment();
  }, []);

  const processPayment = async () => {
    const urlStatus = searchParams.get('status');
    const token = searchParams.get('token');
    const urlType = searchParams.get('type');
    
    console.log('Payment callback params:', { urlStatus, token, urlType });
    
    if (!token) {
      setStatus('error');
      setMessage('Ödeme bilgisi bulunamadı.');
      return;
    }

    // First check payment status
    try {
      const statusResponse = await axios.get(`${API}/payment/status`, {
        params: { token }
      });
      
      console.log('Payment status:', statusResponse.data);
      
      // If payment is already completed, just show success
      if (statusResponse.data.success && statusResponse.data.status === 'completed') {
        setStatus('success');
        setPaymentType(statusResponse.data.payment_type);
        setSuccessMessage(statusResponse.data.payment_type);
        refreshUserData();
        return;
      }
      
      // If payment session exists but not completed (pending or callback_received), try to complete it
      const sessionStatus = statusResponse.data.status;
      if (['pending', 'callback_received'].includes(sessionStatus) && (urlStatus === 'success' || urlType)) {
        console.log('Payment pending/callback_received, attempting to complete...');
        
        const authToken = localStorage.getItem('token');
        if (authToken) {
          try {
            const completeResponse = await axios.post(
              `${API}/payment/complete?token=${token}`,
              {},
              { headers: { Authorization: `Bearer ${authToken}` } }
            );
            
            console.log('Payment complete response:', completeResponse.data);
            
            if (completeResponse.data.success) {
              setStatus('success');
              setPaymentType(completeResponse.data.payment_type);
              setSuccessMessage(completeResponse.data.payment_type);
              refreshUserData();
              return;
            }
          } catch (completeError) {
            console.error('Error completing payment:', completeError);
          }
        }
      }
      
      // Fallback: use URL params
      if (urlStatus === 'success') {
        setStatus('success');
        setPaymentType(urlType);
        setSuccessMessage(urlType);
      } else if (urlStatus === 'failure' || urlStatus === 'failed') {
        setStatus('error');
        const errorMessage = searchParams.get('message');
        setMessage(errorMessage || 'Ödeme başarısız oldu. Lütfen tekrar deneyin.');
      } else {
        setStatus('error');
        setMessage('Ödeme durumu belirlenemedi.');
      }
      
    } catch (error) {
      console.error('Error checking payment:', error);
      
      // If we got success in URL, trust it
      if (urlStatus === 'success') {
        setStatus('success');
        setPaymentType(urlType);
        setSuccessMessage(urlType);
      } else {
        setStatus('error');
        setMessage('Ödeme doğrulanamadı.');
      }
    }
  };

  const setSuccessMessage = (type) => {
    if (type === 'premium') {
      setMessage('Premium üyeliğiniz başarıyla aktifleştirildi! 🎉');
    } else if (type === 'lesson') {
      setMessage('Ders satın alma işleminiz tamamlandı! 🎉');
    } else {
      setMessage('Ödeme işleminiz başarıyla tamamlandı!');
    }
    
    // Google Ads Conversion Tracking
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17858584635/1SyVCNyyud4bELvA0cMC',
        'value': 1.0,
        'currency': 'TRY',
        'transaction_id': searchParams.get('token') || ''
      });
      console.log('Google Ads conversion tracked');
    }
  };

  const refreshUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const user = response.data;
        localStorage.setItem('user', JSON.stringify(user));
        console.log('User data refreshed:', user);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {status === 'loading' && 'Ödeme Doğrulanıyor'}
            {status === 'success' && '🎉 Hoş Geldin Kulübe!'}
            {status === 'error' && 'Ödeme Başarısız'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {status === 'loading' && (
            <Loader2 className="h-16 w-16 mx-auto text-purple-600 animate-spin" />
          )}
          
          {status === 'success' && (
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <CheckCircle className="h-20 w-20 text-green-500" />
                <Sparkles className="h-6 w-6 text-yellow-500 absolute -top-1 -right-1 animate-pulse" />
              </div>
              
              {/* Welcome to Club Message */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  <span className="font-semibold text-purple-800">Pilatika Ailesine Katıldın!</span>
                </div>
                <p className="text-sm text-gray-600">
                  {paymentType === 'premium' 
                    ? 'Premium üye olarak tüm özelliklere erişebilirsin. Sağlıklı yaşam yolculuğun başlıyor!' 
                    : 'Yeni dersini keşfetmeye hazır mısın? Eğitmeninle birlikte harika bir yolculuk seni bekliyor.'}
                </p>
              </div>

              {paymentType === 'premium' && (
                <Gift className="h-8 w-8 text-yellow-500" />
              )}
              {paymentType === 'lesson' && (
                <BookOpen className="h-8 w-8 text-blue-500" />
              )}
            </div>
          )}
          
          {status === 'error' && (
            <XCircle className="h-16 w-16 mx-auto text-red-600" />
          )}

          <p className="text-lg font-medium">{message}</p>
          
          {status === 'success' && paymentType && (
            <p className="text-sm text-gray-500">
              {paymentType === 'premium' 
                ? '✨ AI Diyetisyen, sınırsız ders erişimi ve daha fazlası!' 
                : '▶️ Kursunu hemen izlemeye başlayabilirsin.'}
            </p>
          )}

          <div className="flex gap-4 justify-center flex-wrap">
            {status === 'success' ? (
              <>
                {paymentType === 'premium' ? (
                  <>
                    <Button onClick={() => navigate('/ai-dietitian')} className="btn-primary">
                      AI Diyetisyen&apos;e Git
                    </Button>
                    <Button onClick={() => navigate('/community')} variant="outline">
                      Topluluğa Katıl
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => navigate('/my-courses')} className="btn-primary">
                      Dersime Git
                    </Button>
                    <Button onClick={() => navigate('/community')} variant="outline">
                      Topluluğa Katıl
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <Button onClick={() => navigate('/lessons')} variant="outline">
                  Derslere Dön
                </Button>
                <Button onClick={() => navigate('/')} className="btn-primary">
                  Ana Sayfa
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
